import { useEffect, useEffectEvent, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { useBagAnalytics } from 'features/cart/hooks/useBagAnalytics'
import { EVENT_NAMES } from 'services/Analytics/Analytics'
import type { CartUpsellCardData } from '../cartUpsells.types'
import { CartUpsellCard } from './CartUpsellCard'
import * as S from './CartUpsellCarousel.styles'

type CartUpsellCarouselProps = {
  cards: CartUpsellCardData[]
  onCardClick: (card: CartUpsellCardData, position: number) => void
  onCardDismiss: (card: CartUpsellCardData, position: number) => void
}

export function CartUpsellCarousel({
  cards,
  onCardClick,
  onCardDismiss,
}: CartUpsellCarouselProps) {
  const hasMultipleCards = cards.length > 1
  // The wheel plugin drives it from a trackpad's horizontal swipe and shift+wheel on desktop.
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { align: 'center', watchDrag: hasMultipleCards },
    [WheelGesturesPlugin({ active: hasMultipleCards })]
  )
  const [selectedPosition, setSelectedPosition] = useState(0)
  const { trackBagEvent } = useBagAnalytics()
  const onCarouselSelect = useEffectEvent((toPosition: number) => {
    setSelectedPosition(toPosition)
    trackBagEvent(EVENT_NAMES.CART_UPSELL_CAROUSEL_SWIPED, {
      ...cards[toPosition].analyticsProps,
      'To Position': toPosition,
    })
  })
  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => onCarouselSelect(emblaApi.selectedScrollSnap())
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi])
  return (
    <S.Carousel
      ref={emblaRef}
      // The bag's mobile drawer claims touch moves for swipe-to-dismiss, which would starve embla's drag.
      data-base-ui-swipe-ignore={hasMultipleCards || undefined}
    >
      <S.Track>
        {cards.map((card, position) => (
          <S.Slide key={card.id}>
            <CartUpsellCard
              card={card}
              position={position}
              onClick={() => onCardClick(card, position)}
              onDismiss={() => onCardDismiss(card, position)}
            />
          </S.Slide>
        ))}
      </S.Track>
      {hasMultipleCards && (
        <S.Dots>
          {cards.map((card, position) => (
            <S.Dot
              key={card.id}
              aria-current={position === selectedPosition}
              onClick={() => emblaApi?.scrollTo(position)}
              data-testid="cart-upsell-carousel-dot"
            />
          ))}
        </S.Dots>
      )}
    </S.Carousel>
  )
}
